import { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { User } from "@supabase/supabase-js";

const AuthConfirmPage = () => {
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [userType, setUserType] = useState<"candidate" | "company" | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleOAuthProfileCreation = async (
    user: User, 
    pendingUserType: string
  ): Promise<{ success: boolean; needsProfileCompletion: boolean }> => {
    try {
      if (pendingUserType === 'candidate') {
        // Verificar se já tem perfil de candidato
        const { data: existingProfile } = await supabase
          .from('candidate_profiles')
          .select('id, profile_completed')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (existingProfile) {
          return { success: true, needsProfileCompletion: !existingProfile.profile_completed };
        }
        
        // Verificar se já tem perfil de empresa (conflito)
        const { data: companyProfile } = await supabase
          .from('company_profiles')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (companyProfile) {
          // Usuário já é empresa - fazer logout e mostrar erro
          await supabase.auth.signOut();
          toast({ 
            title: "Conta já existe como Empresa",
            description: "Este email já está cadastrado como empresa. Faça login como empresa.",
            variant: "destructive" 
          });
          return { success: false, needsProfileCompletion: false };
        }
        
        // Criar perfil de candidato
        const { error: profileError } = await supabase
          .from('candidate_profiles')
          .insert({
            user_id: user.id,
            full_name: user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0] || '',
            phone: '',
            cpf: '', // Será preenchido na página de perfil
          });
        
        if (profileError) throw profileError;
        
        // Criar role
        await supabase.from('user_roles').insert({
          user_id: user.id,
          role: 'candidate',
        });
        
        return { success: true, needsProfileCompletion: true };
      } else if (pendingUserType === 'company') {
        // Verificar se já tem perfil de empresa
        const { data: existingProfile } = await supabase
          .from('company_profiles')
          .select('id, profile_completed')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (existingProfile) {
          return { success: true, needsProfileCompletion: !existingProfile.profile_completed };
        }
        
        // Verificar se já tem perfil de candidato (conflito)
        const { data: candidateProfile } = await supabase
          .from('candidate_profiles')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (candidateProfile) {
          // Usuário já é candidato - fazer logout e mostrar erro
          await supabase.auth.signOut();
          toast({ 
            title: "Conta já existe como Candidato",
            description: "Este email já está cadastrado como candidato. Faça login como candidato.",
            variant: "destructive" 
          });
          return { success: false, needsProfileCompletion: false };
        }
        
        // Criar perfil de empresa com créditos iniciais
        const { error: profileError } = await supabase
          .from('company_profiles')
          .insert({
            user_id: user.id,
            company_name: user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0] || '',
            cnpj: '', // Será preenchido na página de perfil
            phone: '',
            responsible_name: user.user_metadata?.full_name || user.user_metadata?.name || '',
            is_verified: true,
            credits: 5,
            credits_expires_at: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
          });
        
        if (profileError) throw profileError;
        
        // Criar role
        await supabase.from('user_roles').insert({
          user_id: user.id,
          role: 'company',
        });
        
        return { success: true, needsProfileCompletion: true };
      }
      
      return { success: false, needsProfileCompletion: false };
    } catch (error) {
      console.error('Error creating OAuth profile:', error);
      return { success: false, needsProfileCompletion: false };
    }
  };

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      try {
        // Check if page was accessed via email link (has valid URL parameters)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get("access_token");
        const type = hashParams.get("type");
        
        // Verificar se é retorno do OAuth Google
        const pendingGoogleUserType = localStorage.getItem('pendingGoogleUserType');
        
        // If no access token and not a signup confirmation and not OAuth, redirect to login
        if (!accessToken && type !== "signup" && !pendingGoogleUserType) {
          navigate("/entrar", { replace: true });
          return;
        }

        // Get the current session after email confirmation
        const {
          data: {
            session
          },
          error
        } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Error getting session:", error);
          setStatus("error");
          setErrorMessage("Erro ao verificar sessão.");
          return;
        }
        
        if (session?.user) {
          // Se é um login OAuth com tipo pendente
          if (pendingGoogleUserType) {
            const result = await handleOAuthProfileCreation(session.user, pendingGoogleUserType);
            localStorage.removeItem('pendingGoogleUserType');
            
            if (result.success) {
              localStorage.setItem('userContext', pendingGoogleUserType);
              setUserType(pendingGoogleUserType as 'candidate' | 'company');
              setStatus("success");
              
              toast({
                title: "Login realizado!",
                description: "Bem-vindo!"
              });
              
              // Redirecionar para completar perfil ou dashboard
              setTimeout(() => {
                if (result.needsProfileCompletion) {
                  navigate(pendingGoogleUserType === 'candidate' 
                    ? '/candidato/minha-conta' 
                    : '/empresa/minha-conta');
                } else {
                  navigate(pendingGoogleUserType === 'candidate' 
                    ? '/' 
                    : '/empresa/dashboard');
                }
              }, 1500);
              return;
            } else {
              setStatus("error");
              setErrorMessage("Não foi possível completar o login. Verifique se você não tem uma conta de outro tipo com este email.");
              return;
            }
          }
          
          // Fluxo normal de confirmação de email
          const userMeta = session.user.user_metadata;
          const userTypeFromMeta = userMeta?.user_type as "candidate" | "company" | undefined;
          if (userTypeFromMeta) {
            setUserType(userTypeFromMeta);
          }
          setStatus("success");

          // Auto redirect after 3 seconds
          setTimeout(() => {
            navigate("/entrar");
          }, 3000);
        } else {
          // Check URL for error indicators
          const errorParam = hashParams.get("error");
          if (errorParam) {
            setStatus("error");
            setErrorMessage("Link inválido ou expirado.");
          } else {
            // No session but no error - might need to login
            setStatus("success");
          }
        }
      } catch (err) {
        console.error("Error during email confirmation:", err);
        setStatus("error");
        setErrorMessage("Ocorreu um erro inesperado.");
      }
    };
    handleEmailConfirmation();
  }, [navigate, toast]);

  const getLoginLink = () => {
    return "/entrar";
  };
  const getLoginLabel = () => {
    return "Fazer Login";
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 py-12 bg-muted/30 flex items-center">
        <div className="container max-w-md">
          <Card>
            {status === "loading" && (
              <>
                <CardHeader className="text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                    <Loader2 className="h-8 w-8 text-primary animate-spin" />
                  </div>
                  <CardTitle className="text-2xl font-display">Confirmando...</CardTitle>
                  <CardDescription className="text-base">
                    Aguarde enquanto verificamos sua conta
                  </CardDescription>
                </CardHeader>
              </>
            )}

            {status === "success" && (
              <>
                <CardHeader className="text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <CardTitle className="text-2xl font-display text-green-600">
                    Confirmado!
                  </CardTitle>
                  <CardDescription className="text-base">
                    Sua conta foi verificada com sucesso. Agora você pode acessar a plataforma.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground text-center pb-[20px]">
                    Você será redirecionado automaticamente em alguns segundos...
                  </p>
                  <Link to={getLoginLink()}>
                    <Button className="w-full">
                      {getLoginLabel()}
                    </Button>
                  </Link>
                </CardContent>
              </>
            )}

            {status === "error" && (
              <>
                <CardHeader className="text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                    <XCircle className="h-8 w-8 text-red-600" />
                  </div>
                  <CardTitle className="text-2xl font-display text-red-600">
                    Erro na confirmação
                  </CardTitle>
                  <CardDescription className="text-base">
                    {errorMessage || "Não foi possível confirmar seu e-mail. O link pode ter expirado ou já foi utilizado."}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground text-center">
                    Tente fazer login ou cadastre-se novamente.
                  </p>
                  <div className="flex flex-col gap-2">
                    <Link to="/entrar">
                      <Button variant="outline" className="w-full">
                        Fazer Login
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </>
            )}
          </Card>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AuthConfirmPage;
